import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Send, ArrowLeft, Wifi, WifiOff, ChevronDown, CheckCircle, X as XIcon } from 'lucide-react';
import { messagingAPI } from '../utils/api';
import { toast } from 'sonner';
import { useMessagingWebSocket } from '../hooks/useMessagingWebSocket';
import ConversationSidebar from '../components/ConversationSidebar';
import MessageList from '../components/MessageList';

const ST = {
  open: { label: 'Open', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  active: { label: 'Open', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  awaiting_payment: { label: 'Awaiting Payment', color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
  pending_payment: { label: 'Awaiting Payment', color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
  pending: { label: 'Pending', color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
  purchased: { label: 'Purchased', color: 'bg-green-500/15 text-green-400 border-green-500/30' },
  confirmed: { label: 'Purchased', color: 'bg-green-500/15 text-green-400 border-green-500/30' },
  paid: { label: 'Purchased', color: 'bg-green-500/15 text-green-400 border-green-500/30' },
  sold: { label: 'Sold', color: 'bg-green-500/15 text-green-400 border-green-500/30' },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/15 text-red-400 border-red-500/30' },
  sold_to_other: { label: 'Sold', color: 'bg-red-500/15 text-red-400 border-red-500/30' },
};

const WatchRow = ({ watch, onAction, loading, isActive, onSelect }) => {
  const st = ST[watch.status] || ST.open;
  const [menuOpen, setMenuOpen] = useState(false);
  const isActionable = ['open', 'active', 'awaiting_payment', 'pending_payment', 'pending'].includes(watch.status);

  return (
    <div className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${isActive ? 'bg-primary/5 border-primary/30' : 'bg-card border-border/50 hover:border-border'}`}
      onClick={() => onSelect?.(watch.conv_id)}>
      {watch.image && <img src={watch.image} alt="" className="w-8 h-8 rounded object-cover shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium truncate">{watch.title || watch.watch_ref?.name}</p>
        <p className="text-[11px] text-[#D0B36B]">${watch.price?.toLocaleString() || watch.watch_ref?.price?.toLocaleString()}</p>
      </div>
      {watch.unread > 0 && <Badge className="bg-primary text-primary-foreground text-[9px] h-4 px-1">{watch.unread}</Badge>}
      <Badge variant="outline" className={`text-[9px] h-4 px-1.5 shrink-0 ${st.color}`}>{st.label}</Badge>
      {isActionable && (
        <div className="relative shrink-0" onClick={e => e.stopPropagation()}>
          <Button size="sm" onClick={() => setMenuOpen(o => !o)} disabled={loading}
            className="bg-muted/60 hover:bg-muted text-foreground h-6 text-[10px] gap-1 px-2">
            Actions <ChevronDown className="h-3 w-3" />
          </Button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-[9998]" onClick={() => setMenuOpen(false)} />
              <div className="fixed z-[9999] bg-card border border-border rounded-lg shadow-2xl py-1 min-w-[180px]"
                style={{ top: 'auto', left: 'auto' }}
                ref={el => {
                  if (el) {
                    const btn = el.previousElementSibling?.previousElementSibling;
                    if (btn) {
                      const rect = btn.getBoundingClientRect();
                      el.style.top = `${rect.bottom + 4}px`;
                      el.style.left = `${Math.min(rect.right - 180, window.innerWidth - 200)}px`;
                    }
                  }
                }}>
                <button onClick={() => { onAction('purchased', watch.conv_id); setMenuOpen(false); }} disabled={loading}
                  className="w-full text-left px-3 py-2.5 text-xs hover:bg-green-500/10 text-green-400 flex items-center gap-2 transition-colors">
                  <CheckCircle className="h-3.5 w-3.5" /> Move to Purchased
                </button>
                <button onClick={() => { onAction('cancelled', watch.conv_id); setMenuOpen(false); }} disabled={loading}
                  className="w-full text-left px-3 py-2.5 text-xs hover:bg-red-500/10 text-red-400 flex items-center gap-2 transition-colors">
                  <XIcon className="h-3.5 w-3.5" /> Cancel Order
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const Messages = () => {
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [watchesOpen, setWatchesOpen] = useState(true);
  const seenMsgIds = useRef(new Set());

  // Derived values
  const activeConv = conversations.find(c => c.id === activeConvId || c.conversations?.some(cc => cc.id === activeConvId));
  const activeMessageConvId = activeConv?.conversations?.find(cc => cc.id === activeConvId) ? activeConvId : activeConv?.id;
  const watches = activeConv?.watches || [];
  const unreadTotal = conversations.reduce((s, c) => s + (c.unread_admin || 0), 0);

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      const data = await messagingAPI.getConversations();
      setConversations(data);
      if (!activeConvId && data.length > 0) setActiveConvId(data[0].id);
    } catch (e) { console.error('Load conversations failed:', e.message); }
    finally { setLoading(false); }
  }, [activeConvId]);

  useEffect(() => { loadConversations(); const i = setInterval(loadConversations, 15000); return () => clearInterval(i); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load messages for active conversation
  useEffect(() => {
    if (!activeMessageConvId) return;
    const load = async () => {
      try {
        const data = await messagingAPI.getMessages(activeMessageConvId);
        setMessages(data);
        data.forEach(m => seenMsgIds.current.add(m.id));
      } catch (e) { console.error('Load messages failed:', e.message); }
    };
    messagingAPI.markRead(activeMessageConvId).then(() => {
      setConversations(prev => prev.map(c => ({
        ...c,
        watches: c.watches?.map(w => w.conv_id === activeMessageConvId ? { ...w, unread: 0 } : w) || [],
        unread_admin: c.watches?.reduce((sum, w) => sum + (w.conv_id === activeMessageConvId ? 0 : (w.unread || 0)), 0) || 0,
      })));
    }).catch(e => console.warn('Mark read failed:', e.message));
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
  }, [activeMessageConvId]); // eslint-disable-line react-hooks/exhaustive-deps

  // WebSocket
  const handleNewMessage = useCallback((data) => {
    const msg = data.message;
    const msgId = msg.id || msg._id;
    if (msgId && !seenMsgIds.current.has(msgId)) {
      seenMsgIds.current.add(msgId);
      const convId = data.conversationId || msg.conversationId;
      if (convId === activeMessageConvId) {
        setMessages(prev => [...prev, {
          id: msgId, conversation_id: convId, sender_id: msg.senderId,
          sender_role: msg.senderRole === 'ADMIN' || msg.senderName === 'Admin User' ? 'admin' : 'user',
          sender_name: msg.senderName || 'User', content: msg.content || '',
          watch_ref: msg.watchRef || null, created_at: msg.createdAt,
        }]);
      }
      loadConversations();
    }
  }, [activeMessageConvId, loadConversations]);

  const { status: wsStatus, connected: wsConnected } = useMessagingWebSocket(handleNewMessage, loadConversations, loadConversations);

  // Actions
  const handleSend = async () => {
    const text = newMsg.trim();
    if (!text || !activeMessageConvId) return;
    setSending(true);
    try { await messagingAPI.sendMessage(activeMessageConvId, text); setNewMsg(''); }
    catch (e) { console.error('Send failed:', e.message); toast.error('Failed to send'); }
    finally { setSending(false); }
    loadConversations();
    const data = await messagingAPI.getMessages(activeMessageConvId);
    setMessages(data);
  };

  const handleWatchAction = async (action, convId) => {
    setActionLoading(true);
    try {
      if (action === 'purchased') {
        const result = await messagingAPI.markPurchased(convId);
        toast.success(result?.order?.orderNumber ? `Purchased! Order: ${result.order.orderNumber}` : 'Marked as purchased!');
      } else if (action === 'cancelled') {
        await messagingAPI.cancelOrder(convId);
        toast.success('Order cancelled');
      }
      setConversations(prev => prev.map(c => ({
        ...c, watches: c.watches?.map(w => w.conv_id === convId ? { ...w, status: action } : w) || [],
      })));
      setTimeout(loadConversations, 1500);
    } catch (e) { toast.error(e.message?.substring(0, 50) || 'Action failed'); }
    finally { setActionLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="text-muted-foreground animate-pulse">Loading messages...</div></div>;

  return (
    <div data-testid="messages-page">
      <div className="mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Messages</h1>
          <span className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${
            wsStatus === 'Live'
              ? 'bg-green-500/15 text-green-400'
              : wsStatus === 'Connecting'
                ? 'bg-yellow-500/15 text-yellow-400'
                : 'bg-red-500/15 text-red-400'
          }`} data-testid="ws-status">
            {wsConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />} {wsStatus}
          </span>
        </div>
        <p className="text-muted-foreground mt-1 text-sm">Customer conversations & purchase flow</p>
      </div>

      <Card className="overflow-hidden border border-border" style={{ height: 'calc(100vh - 200px)', minHeight: '480px' }}>
        <div className="flex h-full">
          {/* Sidebar */}
          <div className={`${mobileShowChat ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 lg:w-96 border-r border-border shrink-0`}>
            <ConversationSidebar conversations={conversations} activeConvId={activeConvId}
              onSelect={(id) => { setActiveConvId(id); setMobileShowChat(true); }}
              search={search} onSearchChange={setSearch} unreadTotal={unreadTotal} />
          </div>

          {/* Chat panel */}
          <div className={`${mobileShowChat ? 'flex' : 'hidden md:flex'} flex-col flex-1 min-w-0`}>
            {activeConv ? (
              <>
                <div className="px-4 py-3 border-b border-border bg-muted/20">
                  <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" className="md:hidden h-8 w-8" onClick={() => setMobileShowChat(false)}><ArrowLeft className="h-4 w-4" /></Button>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{activeConv.user_name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{activeConv.user_email}</p>
                    </div>
                  </div>
                  {watches.length > 0 && (
                    <div className="mt-2">
                      <button onClick={() => setWatchesOpen(o => !o)} className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground w-full">
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${watchesOpen ? '' : '-rotate-90'}`} />
                        {watches.length} watch{watches.length > 1 ? 'es' : ''} ({watches.filter(w => ['active','open'].includes(w.status)).length} active, {watches.filter(w => w.status === 'purchased').length} purchased)
                      </button>
                      {watchesOpen && (
                        <div className="space-y-1.5 mt-1.5 max-h-[200px] overflow-y-auto">
                          {watches.map((w, i) => (
                            <WatchRow key={w.conv_id || i} watch={w} onAction={handleWatchAction}
                              onSelect={(id) => setActiveConvId(id)} isActive={w.conv_id === activeMessageConvId} loading={actionLoading} />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <MessageList messages={messages} />

                <div className="p-3 border-t border-border bg-muted/10">
                  <form className="flex gap-2" onSubmit={e => { e.preventDefault(); handleSend(); }}>
                    <Input value={newMsg} onChange={e => setNewMsg(e.target.value)} placeholder="Type your reply..." className="flex-1 text-sm" data-testid="message-input" disabled={sending} />
                    <Button type="submit" size="icon" disabled={sending || !newMsg.trim()} className="bg-primary text-primary-foreground hover:bg-primary/80 shrink-0" data-testid="send-button"><Send className="h-4 w-4" /></Button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2"><span className="text-sm">Select a conversation</span></div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Messages;
